import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { HourglassMedium, CheckCircle, X, FilePdf } from "@phosphor-icons/react";

export default function PendingPayments() {
  const { storeId } = useParams();
  const [list, setList] = useState([]);
  const [settling, setSettling] = useState(null);

  const load = async () => {
    const r = await api.get(`/store/${storeId}/sales/pending`);
    setList(r.data || []);
  };
  useEffect(() => { load(); }, [storeId]);

  const openPdf = async (id) => {
    const token = localStorage.getItem("pos_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/sales/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const totalPending = useMemo(() => list.reduce((a, s) => a + (s.pending_amount || 0), 0), [list]);

  return (
    <Shell>
      <PageHeader
        title="Pending Payments"
        subtitle="Invoices billed with a pending balance. Record payments here as money comes in."
        testId="pending-header"
      />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-slate-900 text-white rounded-md p-5" data-testid="pending-total-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold"><HourglassMedium size={14} /> Total Pending</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2" data-testid="pending-total">{inr(totalPending)}</div>
            <div className="text-[10px] text-slate-400 mt-1">{list.length} invoice{list.length === 1 ? "" : "s"} awaiting payment</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="pending-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Invoice #</th>
                <th className="text-left px-4 py-3 font-semibold">Date (IST)</th>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-right px-4 py-3 font-semibold">Total</th>
                <th className="text-right px-4 py-3 font-semibold">Paid</th>
                <th className="text-right px-4 py-3 font-semibold">Pending</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-400" data-testid="pending-empty">No pending payments — all invoices are settled.</td></tr>
              )}
              {list.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pending-row-${s.invoice_no}`}>
                  <td className="px-4 py-3 font-mono-num font-semibold">{s.invoice_no}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{fmtIST(s.created_at)}</td>
                  <td className="px-4 py-3">{s.customer_name || "Walk-in"}</td>
                  <td className="px-4 py-3 font-mono-num text-slate-600">{s.customer_phone || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{inr(s.grand_total)}</td>
                  <td className="px-4 py-3 text-right font-mono-num text-emerald-700">{inr(s.paid_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-bold text-amber-700" data-testid={`pending-amt-${s.invoice_no}`}>{inr(s.pending_amount)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openPdf(s.id)} data-testid={`pending-pdf-${s.invoice_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"><FilePdf size={14} />PDF</button>
                    <button onClick={() => setSettling(s)} data-testid={`pending-receive-${s.invoice_no}`} className="h-8 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-md inline-flex items-center gap-1">
                      <CheckCircle size={13} weight="bold" /> Record Payment
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {settling && (
        <ReceivePaymentModal
          sale={settling}
          onClose={() => setSettling(null)}
          onDone={() => { setSettling(null); load(); }}
        />
      )}
    </Shell>
  );
}

function ReceivePaymentModal({ sale, onClose, onDone }) {
  const [amount, setAmount] = useState(String(sale.pending_amount));
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) { setError("Enter an amount greater than zero."); return; }
    setSaving(true); setError("");
    try {
      await api.post(`/store/sales/${sale.id}/receive-payment`, { amount: amt, method, reference });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="receive-payment-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Record Payment</div>
            <div className="text-xs text-slate-500 font-mono-num">{sale.invoice_no} · {sale.customer_name || "Walk-in"}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Invoice Total</div>
              <div className="font-display font-bold font-mono-num mt-1">{inr(sale.grand_total)}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">Pending</div>
              <div className="font-display font-bold font-mono-num mt-1 text-amber-800" data-testid="modal-pending-amount">{inr(sale.pending_amount)}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Amount Received (₹) *</label>
              <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="receive-amount" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" autoFocus />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="receive-method" className="w-full h-10 px-2 border border-slate-300 rounded-md text-sm uppercase font-semibold">
                {["cash", "upi", "card", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          {(method === "upi" || method === "card" || method === "other") && (
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Reference</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="receive-reference" placeholder="Txn ID / last 4 digits" className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
            </div>
          )}
          <div className="text-[11px] text-slate-500">
            Partial amounts are allowed — the remaining balance stays pending. Cash received here appears in the Cash Register on today's date.
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-2" data-testid="receive-error">{error}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="receive-save" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
            <CheckCircle size={14} weight="bold" /> {saving ? "Saving…" : "Record Payment"}
          </button>
        </div>
      </div>
    </div>
  );
}
